import { getThumbnail } from '@/services/filters';
import { uploadSingleAttachment, deleteAttachment } from '@/api/attachment';

/**
 * FileGridMixin - Reusable mixin for handling file/image management with FileGrid component
 *
 * Usage:
 * 1. Import and add to mixins array
 * 2. Call fileGridInitialize() with existing IDs
 * 3. Use fileGrid.dialogVisible to control dialog
 * 4. Call fileGridProcessChanges() during save to handle uploads
 */
export default {
  data() {
    return {
      fileGrid: {
        tempDocuments: [], // Documents shown in FileGrid
        pendingFiles: [], // New files to upload
        removedIds: [], // IDs marked for removal
        originalIds: [], // Original IDs for comparison
        dialogVisible: false, // Dialog visibility
        maxFiles: 5, // Maximum allowed files
        isProcessing: false, // Upload processing state
        acceptTypes: 'image/jpeg,image/png,image/jpg', // Accepted file types
      },
    };
  },

  computed: {
    fileGridHasChanges() {
      return this.fileGrid.pendingFiles.length > 0 || this.fileGrid.removedIds.length > 0;
    },

    fileGridDocumentCount() {
      return this.fileGrid.tempDocuments.length;
    },

    fileGridCanAddMore() {
      return this.fileGrid.tempDocuments.length < this.fileGrid.maxFiles;
    },

    fileGridRemainingSlots() {
      return this.fileGrid.maxFiles - this.fileGrid.tempDocuments.length;
    },
  },

  methods: {
    /**
     * Initialize file grid with existing IDs
     * @param {Array} existingIds - Array of existing file/image IDs
     * @param {Object} options - Configuration options
     */
    fileGridInitialize(existingIds = [], options = {}) {
      // Store options
      this.fileGrid.maxFiles = options.maxFiles || 5;
      this.fileGrid.acceptTypes = options.acceptTypes || 'image/jpeg,image/png,image/jpg';

      // Store original IDs for comparison
      this.fileGrid.originalIds = [...existingIds];

      // Reset state
      this.fileGrid.tempDocuments = [];
      this.fileGrid.pendingFiles = [];
      this.fileGrid.removedIds = [];

      // Convert existing IDs to FileGrid documents
      existingIds.forEach((id, index) => {
        if (id && !this.fileGrid.removedIds.includes(id)) {
          this.fileGrid.tempDocuments.push(
            this.fileGridCreateDocument(id, `Image ${index + 1}`, true),
          );
        }
      });

      // Add any pending files that were selected before
      this.fileGrid.pendingFiles.forEach((file, index) => {
        this.fileGrid.tempDocuments.push(
          this.fileGridCreateDocument(file, file.name, false, `new-${Date.now()}-${index}`),
        );
      });
    },

    /**
     * Create a document object for FileGrid
     */
    fileGridCreateDocument(source, filename, isExisting = false, tmpId = null) {
      const api = this.$api || this.api || window.api;

      if (isExisting) {
        // Existing image from server
        return {
          localTmpId: `existing-${source}`,
          _id: source,
          thumbnail: getThumbnail(api, source, 512),
          src: getThumbnail(api, source, 512),
          contentType: 'image/jpeg',
          filename: filename,
          isExisting: true,
        };
      } else {
        // New file to upload
        return {
          localTmpId: tmpId || `new-${Date.now()}`,
          file: source,
          thumbnail: URL.createObjectURL(source),
          src: URL.createObjectURL(source),
          contentType: source.type,
          filename: filename,
          isNew: true,
        };
      }
    },

    /**
     * Handle file selection
     * @param {Event} event - File input change event
     */
    fileGridOnFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      this.fileGridAddFiles(files);
    },

    /**
     * Add new files to the grid
     * @param {Array} files - Array of File objects
     */
    fileGridAddFiles(files) {
      const availableSlots = this.fileGrid.maxFiles - this.fileGrid.tempDocuments.length;

      if (availableSlots <= 0) {
        this.fileGridShowMessage('Maximum files reached', 'warning');
        return;
      }

      const filesToAdd = files.slice(0, availableSlots);

      filesToAdd.forEach(file => {
        const tmpId = `new-${Date.now()}-${Math.random()}`;
        this.fileGrid.tempDocuments.push(
          this.fileGridCreateDocument(file, file.name, false, tmpId),
        );
      });

      if (files.length > availableSlots) {
        this.fileGridShowMessage(
          `Only ${availableSlots} of ${files.length} files were added. Maximum ${this.fileGrid.maxFiles} files allowed.`,
          'warning',
        );
      }
    },

    /**
     * Remove a document from the grid
     * @param {Object} doc - Document to remove
     */
    fileGridRemoveDocument(doc) {
      const index = this.fileGrid.tempDocuments.findIndex(d => d.localTmpId === doc.localTmpId);

      if (index === -1) return;

      // Clean up preview URL if it's a new file
      if (doc.isNew && doc.thumbnail) {
        URL.revokeObjectURL(doc.thumbnail);
      }

      this.fileGrid.tempDocuments.splice(index, 1);
    },

    /**
     * Handle document selection (optional preview functionality)
     * @param {Object} doc - Selected document
     */
    fileGridSelectDocument(doc) {
      // Override in component if needed for preview functionality
    },

    /**
     * Open the file grid dialog
     */
    fileGridOpenDialog() {
      const api = this.$api || this.api || window.api;
      // Re-initialize to ensure fresh state
      this.fileGridInitialize(this.fileGrid.originalIds);
      this.fileGrid.dialogVisible = true;
    },

    /**
     * Close the dialog and clean up
     */
    fileGridCloseDialog() {
      this.fileGridCleanupPreviews();
      this.fileGrid.tempDocuments = [];
      this.fileGrid.dialogVisible = false;
    },

    /**
     * Confirm changes made in the dialog
     */
    fileGridConfirmChanges() {
      // Update pending files based on dialog changes
      this.fileGrid.pendingFiles = this.fileGrid.tempDocuments
        .filter(doc => doc.isNew)
        .map(doc => doc.file);

      // Find removed IDs
      const currentExistingIds = this.fileGrid.tempDocuments
        .filter(doc => doc.isExisting)
        .map(doc => doc._id);

      this.fileGrid.removedIds = this.fileGrid.originalIds.filter(
        id => !currentExistingIds.includes(id),
      );

      this.fileGridCloseDialog();
    },

    /**
     * Process all pending changes (uploads and removals)
     * @returns {Array} Final array of file IDs
     */
    async fileGridProcessChanges() {
      const api = this.$api || this.api || window.api;

      try {
        this.fileGrid.isProcessing = true;

        // Start with existing IDs, excluding removed ones
        let finalIds = this.fileGrid.originalIds.filter(
          id => !this.fileGrid.removedIds.includes(id),
        );

        // Upload new files
        for (const file of this.fileGrid.pendingFiles) {
          try {
            const attachment = await uploadSingleAttachment(api, file);
            finalIds.push(attachment._id);
          } catch (error) {
            console.error('Error uploading file:', error);
            this.fileGridShowMessage('Failed to upload file', 'error');
            throw error;
          }
        }

        // Clear pending changes after successful processing
        this.fileGrid.pendingFiles = [];
        this.fileGrid.removedIds = [];
        this.fileGrid.originalIds = finalIds;

        // Limit to max files
        return finalIds.slice(0, this.fileGrid.maxFiles);
      } finally {
        this.fileGrid.isProcessing = false;
      }
    },

    /**
     * Clean up preview URLs
     */
    fileGridCleanupPreviews() {
      this.fileGrid.tempDocuments.forEach(doc => {
        if (doc.isNew && doc.thumbnail) {
          URL.revokeObjectURL(doc.thumbnail);
        }
      });
    },

    /**
     * Show a message (override this in component to use your alert system)
     */
    fileGridShowMessage(message, type = 'info') {
      // Use component's alert system if available
      if (this.updateAlertMessage) {
        this.updateAlertMessage({ msg: message, type });
      } else if (this.$store) {
        this.$store.dispatch('alert/updateAlertMessage', { msg: message, type });
      } else {
        console.log(`[FileGrid ${type}]:`, message);
      }
    },

    /**
     * Reset all file grid data
     */
    fileGridReset() {
      this.fileGridCleanupPreviews();
      this.fileGrid.tempDocuments = [];
      this.fileGrid.pendingFiles = [];
      this.fileGrid.removedIds = [];
      this.fileGrid.originalIds = [];
      this.fileGrid.dialogVisible = false;
      this.fileGrid.isProcessing = false;
    },
  },

  beforeDestroy() {
    // Clean up any preview URLs when component is destroyed
    this.fileGridCleanupPreviews();
  },
};
